import { zodResolver } from "@hookform/resolvers/zod";
import {
  CreateProjectDocument,
  DeleteProjectDocument,
  OrganizationProjectsDocument,
  ProjectFragment,
  ProjectDetailsDocument,
  ProjectType,
  UpdateProjectDocument,
  UpdateProjectParticipantsDocument,
  OrganizationEventsDocument,
} from "../../../../__generated__/graphql";
import Modal from "../../../../components/Modal";
import { z } from "zod";
import { useForm } from "react-hook-form";
import FormInput from "../../../../components/FormInput";
import Button from "../../../../components/Button";
import { useMutation } from "@apollo/client";
import { toast } from "react-hot-toast";
import { useEffect, useState } from "react";
import { motion } from "motion/react";
import useNav from "../../../../hooks/useNavigation";
import { TrashIcon } from "@heroicons/react/24/outline";
import ManageParticipantsModal from "../../../../components/ManageParticipantsModal";
import FullLoading from "../../../../components/FullLoading";

const projectSchema = z
  .object({
    projectName: z.string().min(1, "Un nom est requis"),
    description: z.string().optional(),
    projectType: z.enum(Object.values(ProjectType) as [string, ...string[]]),
    otherProjectType: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.projectType === ProjectType.Other) {
        return !!data.otherProjectType && data.otherProjectType.trim() !== "";
      }
      return true;
    },
    {
      message: "Veuillez préciser le type de projet",
      path: ["otherProjectType"],
    }
  );

type ProjectFormData = z.infer<typeof projectSchema>;

export default function ProjectModal({
  isOpen,
  onClose,
  defaultProject,
}: {
  isOpen: boolean;
  onClose: () => void;
  defaultProject?: ProjectFragment;
}) {
  const isUpdate = Boolean(defaultProject);
  const navigateWithLog = useNav();

  const [newProjectCreated, setNewProjectCreated] =
    useState<ProjectFragment | null>(null);

  const [createProject, { loading: isCreatingProject }] = useMutation(
    CreateProjectDocument
  );
  const [updateProject, { loading: isUpdatingProject }] = useMutation(
    UpdateProjectDocument
  );
  const [deleteProject, { loading: isDeletingProject }] = useMutation(
    DeleteProjectDocument
  );
  const [updateProjectParticipants] = useMutation(
    UpdateProjectParticipantsDocument
  );

  const isLoading = isCreatingProject || isUpdatingProject || isDeletingProject;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      projectType: ProjectType.Serie,
    },
  });

  const projectType = watch("projectType");

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      reset({
        projectType: defaultProject?.projectType || ProjectType.Serie,
        projectName: defaultProject?.name || "",
        description: defaultProject?.description || "",
        otherProjectType: defaultProject?.otherProjectType || "",
      });
    }
  }, [isOpen, reset, defaultProject]);

  // Handle project type changes
  useEffect(() => {
    if (projectType !== ProjectType.Other) {
      setValue("otherProjectType", "");
    }
  }, [projectType, setValue]);

  const handleDelete = async () => {
    const response = confirm(
      "Voulez-vous vraiment supprimer ce projet ? Cette action supprimera toutes les données associées (évènements, activités, documents, participants...)"
    );
    if (!response) return;

    try {
      await deleteProject({
        variables: {
          projectId: defaultProject?.id || "",
        },
        refetchQueries: [
          OrganizationProjectsDocument,
          OrganizationEventsDocument,
        ],
      });

      navigateWithLog("/projects");
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue");
    }
  };

  const onSubmit = async (data: ProjectFormData) => {
    try {
      if (isUpdate && defaultProject?.id) {
        await updateProject({
          variables: {
            updateProjectInput: {
              projectId: defaultProject.id,
              name: data.projectName,
              description: data.description,
              projectType: data.projectType as ProjectType,
              otherProjectType: data.otherProjectType,
            },
          },
          refetchQueries: [
            {
              query: ProjectDetailsDocument,
              variables: { projectId: defaultProject.id },
            },
          ],
        });

        toast.success("Projet modifié avec succès");
        onClose();
      } else {
        const { data: projectData } = await createProject({
          variables: {
            createProjectInput: {
              projectType: data.projectType as ProjectType,
              name: data.projectName,
              description: data.description,
              otherProjectType: data.otherProjectType,
            },
          },
          refetchQueries: [OrganizationProjectsDocument],
        });

        onClose();

        toast.loading("Projet en cours de création...", {
          duration: 3000,
        });

        if (projectData?.createProject?.id) {
          setNewProjectCreated(projectData.createProject);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue");
    }
  };

  if (isLoading) {
    return <FullLoading />;
  }

  return (
    <>
      {newProjectCreated ? (
        <ManageParticipantsModal
          isOpen={true}
          onClose={() => {
            setNewProjectCreated(null);
            navigateWithLog(`/projects/${newProjectCreated.id}`);
          }}
          onSubmit={async (selectedUserOrganizations) => {
            if (newProjectCreated) {
              try {
                await updateProjectParticipants({
                  variables: {
                    projectId: newProjectCreated.id,
                    participantUserOrganizationIds:
                      selectedUserOrganizations.map(
                        (userOrganization) => userOrganization.id
                      ),
                  },
                });

                navigateWithLog(`/projects/${newProjectCreated.id}`);
              } catch (error) {
                toast.error(
                  error instanceof Error
                    ? error.message
                    : "Une erreur est survenue lors de l'ajout des participants"
                );
              }
            }
          }}
          description="Modifiez la liste des participants pour ce nouveau projet."
          participantsFor="project"
          projectId={newProjectCreated?.id}
        />
      ) : null}

      <Modal
        isModalOpen={isOpen}
        onClose={onClose}
        title={isUpdate ? "Modifier le projet" : "Créer un projet"}
        description="Un projet vous permet d'organiser une suite d'évènements liés entre eux."
        dataTestId="project-modal"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Type de projet
            </label>
            <select
              {...register("projectType")}
              id="projectType"
              data-testid="project-type-select"
              className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border placeholder:text-tertiary"
            >
              <option value={ProjectType.Serie}>Tournée</option>
              <option value={ProjectType.Movie}>Film</option>
              <option value={ProjectType.Album}>Album</option>
              <option value={ProjectType.Other}>Autre</option>
            </select>
          </div>

          {projectType === ProjectType.Other && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <FormInput
                label="Précisez le type de projet"
                error={errors.otherProjectType}
                id="otherProjectType"
                register={register}
              />
            </motion.div>
          )}

          <FormInput
            label="Nom du projet"
            error={errors.projectName}
            id="projectName"
            register={register}
            dataTestId="project-name-input"
          />

          <div>
            <label className="block text-sm font-medium mb-1">
              Description (optionnel)
            </label>
            <textarea
              {...register("description")}
              rows={2}
              className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border"
            />
          </div>

          <div className="flex justify-between space-x-3 pt-4">
            {defaultProject ? (
              <Button
                type="button"
                variant="secondary"
                onClick={handleDelete}
                className="flex items-center gap-2 text-red-400 hover:text-red-400/80"
                dataTestId="project-delete-button"
              >
                <TrashIcon className="w-5 h-5 cursor-pointer flex-shrink-0" />
                Supprimer
              </Button>
            ) : null}

            <Button type="submit" className="w-full" isLoading={isLoading} dataTestId="project-submit-button">
              {isUpdate ? "Modifier" : "Créer"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
